import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { restartForIdentityChange } from '../api/whoami';
import type { WhoamiImpersonation } from '../api/whoami';

interface ImpersonationBannerProps {
  impersonation: WhoamiImpersonation;
  /** The impersonating admin's own email (`whoami.authenticated.email`). */
  adminEmail: string;
}

// Distinct amber treatment lifted from the prototype banner. These are the
// banner's own semantic colors (not customer-brandable chrome), so they are
// intentionally fixed rather than pulled from the theme palette.
const BANNER_BG = '#FCEEC9';
const BANNER_FG = '#5A4310';
const BANNER_TITLE_FG = '#3F2E08';
const BANNER_ACCENT = '#7A5200';
const BANNER_BORDER = 'rgba(224, 161, 6, 0.55)';

function formatElapsed(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

/**
 * Persistent, non-dismissible amber banner shown above the whole shell while an
 * impersonation session is active. It reads `whoami.impersonation` (target +
 * `startedAt`) and the impersonating admin's email, shows a **live elapsed**
 * readout (never a countdown — there is no server expiry), prefixes the browser
 * tab title with `[Impersonating]`, and exposes an always-visible "Exit
 * impersonation" action that ends the session server-side then invalidates
 * `['whoami']` so the shell re-renders as the admin.
 */
export function ImpersonationBanner({ impersonation, adminEmail }: ImpersonationBannerProps) {
  const { t } = useTranslation();
  const startedAtMs = useMemo(
    () => new Date(impersonation.startedAt).getTime(),
    [impersonation.startedAt],
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const prefix = t('impersonation.titlePrefix');
  useEffect(() => {
    const original = document.title;
    document.title = original.startsWith(prefix) ? original : `${prefix} ${original}`;
    return () => {
      document.title = original;
    };
  }, [prefix]);

  const elapsed = formatElapsed(now - startedAtMs);

  const handleExit = async () => {
    try {
      await apiClient.post('/me/impersonation/end');
    } catch {
      // Best-effort: still refresh identity so the UI reflects server state.
    }
    // A full restart, not a cache reset: between the identity flipping and the
    // router landing, the impersonated tree is still mounted and every query it
    // owns is now unauthorized (BUG-046). Reloading is the one option in which the
    // two identities never coexist. `/` re-resolves per identity, so a
    // SiteAdministrator returns to the console instead of being stranded on the
    // customer route the session was viewing.
    restartForIdentityChange();
  };

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
        p: '9px 18px 9px 16px',
        flexShrink: 0,
        bgcolor: BANNER_BG,
        color: BANNER_FG,
        borderTop: '3px solid',
        borderTopColor: '#E0A106',
        borderBottom: '1px solid',
        borderBottomColor: BANNER_BORDER,
      }}
    >
      <Chip
        icon={<VisibilityOutlined />}
        label={t('impersonation.tag')}
        size="small"
        sx={{
          bgcolor: BANNER_ACCENT,
          color: '#fff',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          '& .MuiChip-icon': { color: '#fff' },
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 14,
            color: BANNER_TITLE_FG,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('impersonation.viewingAs', {
            name: impersonation.target.fullName,
            email: impersonation.target.email,
          })}
        </Typography>
        <Typography variant="caption" sx={{ color: BANNER_ACCENT, fontSize: 12, fontWeight: 500 }}>
          {t('impersonation.impersonatedBy', { email: adminEmail })}
        </Typography>
      </Box>
      <Chip
        icon={<AccessTimeOutlined />}
        label={elapsed}
        size="small"
        aria-label={t('impersonation.elapsedLabel')}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          color: BANNER_ACCENT,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          borderRadius: 999,
          border: '1px solid rgba(224, 161, 6, 0.6)',
          '& .MuiChip-icon': { color: BANNER_ACCENT },
        }}
      />
      <Button
        onClick={handleExit}
        startIcon={<LogoutOutlined />}
        variant="contained"
        sx={(theme) => ({
          bgcolor: theme.palette.text.primary,
          color: theme.palette.common.white,
          '&:hover': { bgcolor: '#0A2A47' },
        })}
      >
        {t('impersonation.exit')}
      </Button>
    </Box>
  );
}
